import { useState, useEffect, useCallback } from 'react';
import { vehicleService } from '@/services/vehicleService';
import type { VehicleLocation } from '@/types';
import { useUnifiedAuth } from '@/contexts/UnifiedAuthContext';

export function useVehicles() {
  const { user } = useUnifiedAuth();
  const [vehicles, setVehicles] = useState<VehicleLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    try {
      setLoading(true);
      const data = await vehicleService.list();
      setVehicles(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error loading vehicles');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { fetch(); }, [fetch]);

  const create = useCallback(async (vehicle: Partial<VehicleLocation>) => {
    const data = await vehicleService.create(vehicle);
    setVehicles(prev => [data, ...prev]);
    return data;
  }, []);

  const update = useCallback(async (id: string, updates: Partial<VehicleLocation>) => {
    const data = await vehicleService.update(id, updates);
    setVehicles(prev => prev.map(v => v.id === id ? data : v));
    return data;
  }, []);

  const remove = useCallback(async (id: string) => {
    await vehicleService.remove(id);
    setVehicles(prev => prev.filter(v => v.id !== id));
  }, []);

  return { vehicles, loading, error, refetch: fetch, create, update, remove };
}
